import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, FormControlLabel, Checkbox, Select, MenuItem,
    FormControl, InputLabel, Alert, Accordion, AccordionSummary, AccordionDetails,
    CircularProgress, Link, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { API_BASE } from '../../apiBase';

// Provider documentation links
const PROVIDER_DOCS = {
    openai: 'https://platform.openai.com/docs/models',
    claude: 'https://docs.anthropic.com/en/docs/about-claude/models',
    gemini: 'https://ai.google.dev/gemini-api/docs/models'
};

export default function LLMModelsTab() {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [expandedProvider, setExpandedProvider] = useState(null);
    const [validatingModel, setValidatingModel] = useState(null);
    const [draggedModel, setDraggedModel] = useState(null);

    // Dialogs
    const [providerDialog, setProviderDialog] = useState(null);
    const [modelDialog, setModelDialog] = useState(null);

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
    };

    useEffect(() => {
        fetchProviders();
    }, []);

    const fetchProviders = async () => {
        setLoading(true);
        setMessage('');
        try {
            const res = await fetch(`${API_BASE}/admin/llm-providers`, { headers });
            if (!res.ok) throw new Error('Failed to fetch providers');
            const data = await res.json();
            setProviders(data);
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProvider = () => {
        setProviderDialog({
            mode: 'create',
            data: { name: '', display_name: '', description: '', is_enabled: true, sort_order: 0 }
        });
    };

    const handleEditProvider = (provider) => {
        setProviderDialog({
            mode: 'edit',
            data: { ...provider }
        });
    };

    const handleSaveProvider = async () => {
        const { mode, data } = providerDialog;
        setMessage('');

        try {
            const url = mode === 'create'
                ? `${API_BASE}/admin/llm-providers`
                : `${API_BASE}/admin/llm-providers/${data.id}`;
            const method = mode === 'create' ? 'POST' : 'PATCH';

            const res = await fetch(url, {
                method,
                headers,
                body: JSON.stringify(data)
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to save provider');
            }

            setMessage(`✅ Provider ${mode === 'create' ? 'created' : 'updated'} successfully`);
            setProviderDialog(null);
            fetchProviders();
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        }
    };

    const handleDeleteProvider = async (providerId) => {
        if (!window.confirm('Are you sure? This will delete all models for this provider.')) return;

        setMessage('');
        try {
            const res = await fetch(`${API_BASE}/admin/llm-providers/${providerId}`, {
                method: 'DELETE',
                headers
            });

            if (!res.ok) throw new Error('Failed to delete provider');

            setMessage('✅ Provider deleted successfully');
            fetchProviders();
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        }
    };

    const handleCreateModel = (providerId) => {
        setModelDialog({
            mode: 'create',
            providerId,
            data: {
                name: '',
                display_name: '',
                description: '',
                is_enabled: true,
                is_default: false,
                performance_tier: null,
                efficiency_tier: null,
                cost_tier: null,
                context_window: null,
                supports_vision: false,
                supports_function_calling: false,
                sort_order: 0
            }
        });
    };

    const handleEditModel = (providerId, model) => {
        setModelDialog({
            mode: 'edit',
            providerId,
            data: { ...model }
        });
    };

    const handleSaveModel = async () => {
        const { mode, providerId, data } = modelDialog;
        setMessage('');

        try {
            const url = mode === 'create'
                ? `${API_BASE}/admin/llm-providers/${providerId}/models`
                : `${API_BASE}/admin/llm-models/${data.id}`;
            const method = mode === 'create' ? 'POST' : 'PATCH';

            const res = await fetch(url, {
                method,
                headers,
                body: JSON.stringify(data)
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to save model');
            }

            setMessage(`✅ Model ${mode === 'create' ? 'created' : 'updated'} successfully`);
            setModelDialog(null);
            fetchProviders();
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        }
    };

    const handleDeleteModel = async (modelId) => {
        if (!window.confirm('Are you sure you want to delete this model?')) return;

        setMessage('');
        try {
            const res = await fetch(`${API_BASE}/admin/llm-models/${modelId}`, {
                method: 'DELETE',
                headers
            });

            if (!res.ok) throw new Error('Failed to delete model');

            setMessage('✅ Model deleted successfully');
            fetchProviders();
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        }
    };

    const handleValidateModel = async (modelId) => {
        setValidatingModel(modelId);
        try {
            const res = await fetch(`${API_BASE}/admin/llm-models/${modelId}/validate`, {
                method: 'POST',
                headers
            });

            const result = await res.json();
            if (result.available) {
                setMessage(`✅ ${result.message}`);
            } else {
                setMessage(`⚠️ ${result.message}`);
            }
        } catch (err) {
            setMessage(`❌ Failed to validate: ${err.message}`);
        } finally {
            setValidatingModel(null);
        }
    };

    const handleDragStart = (e, model) => {
        setDraggedModel(model);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, targetModel, providerId) => {
        e.preventDefault();
        if (!draggedModel || draggedModel.id === targetModel.id) {
            setDraggedModel(null);
            return;
        }

        const provider = providers.find(p => p.id === providerId);
        const models = [...provider.models];
        const draggedIndex = models.findIndex(m => m.id === draggedModel.id);
        const targetIndex = models.findIndex(m => m.id === targetModel.id);

        // Reorder
        models.splice(draggedIndex, 1);
        models.splice(targetIndex, 0, draggedModel);

        // Update sort_order
        const model_orders = models.map((m, index) => ({ id: m.id, sort_order: index }));

        try {
            const res = await fetch(`${API_BASE}/admin/llm-models/reorder`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({ model_orders })
            });

            if (!res.ok) throw new Error('Failed to reorder models');

            setMessage('✅ Models reordered successfully');
            fetchProviders();
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        } finally {
            setDraggedModel(null);
        }
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">LLM Providers & Models</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateProvider}>
                    Add Provider
                </Button>
            </Box>

            {message && <Alert severity={message.startsWith('✅') ? 'success' : message.startsWith('⚠️') ? 'warning' : 'error'} sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            {providers.length === 0 ? (
                <Alert severity="info">No providers configured. Add your first provider to get started.</Alert>
            ) : (
                providers.map((provider) => (
                    <Accordion
                        key={provider.id}
                        expanded={expandedProvider === provider.id}
                        onChange={(_, isExpanded) => setExpandedProvider(isExpanded ? provider.id : null)}
                        sx={{ mb: 1 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={2} width="100%">
                                <Typography variant="h6">{provider.display_name}</Typography>
                                <Chip label={provider.name} size="small" />
                                {!provider.is_enabled && <Chip label="Disabled" size="small" color="error" />}
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', mr: 2 }}>
                                    {provider.model_count} models
                                </Typography>
                                {PROVIDER_DOCS[provider.name] && (
                                    <Tooltip title="View API Documentation">
                                        <IconButton
                                            size="small"
                                            component={Link}
                                            href={PROVIDER_DOCS[provider.name]}
                                            target="_blank"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {provider.description || 'No description'}
                                    </Typography>
                                    {PROVIDER_DOCS[provider.name] && (
                                        <Link href={PROVIDER_DOCS[provider.name]} target="_blank" rel="noopener" sx={{ fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                            API Documentation <OpenInNewIcon sx={{ fontSize: 14 }} />
                                        </Link>
                                    )}
                                </Box>
                                <Box display="flex" gap={1}>
                                    <IconButton size="small" onClick={() => handleEditProvider(provider)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDeleteProvider(provider.id)} color="error">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="subtitle2">Models</Typography>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => handleCreateModel(provider.id)}>
                                    Add Model
                                </Button>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={30}></TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Display Name</TableCell>
                                            <TableCell align="center">Default</TableCell>
                                            <TableCell align="center">Perf</TableCell>
                                            <TableCell align="center">Eff</TableCell>
                                            <TableCell align="center">Cost</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                            <TableCell>Added</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {provider.models && provider.models.length > 0 ? (
                                            provider.models.map((model) => (
                                                <TableRow
                                                    key={model.id}
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, model)}
                                                    onDragOver={handleDragOver}
                                                    onDrop={(e) => handleDrop(e, model, provider.id)}
                                                    sx={{
                                                        cursor: 'move',
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                        opacity: draggedModel?.id === model.id ? 0.5 : 1
                                                    }}
                                                >
                                                    <TableCell><DragIndicatorIcon fontSize="small" color="action" /></TableCell>
                                                    <TableCell><code>{model.name}</code></TableCell>
                                                    <TableCell>{model.display_name}</TableCell>
                                                    <TableCell align="center">
                                                        {model.is_default ? <StarIcon color="primary" fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                                    </TableCell>
                                                    <TableCell align="center">{model.performance_tier || '-'}</TableCell>
                                                    <TableCell align="center">{model.efficiency_tier || '-'}</TableCell>
                                                    <TableCell align="center">{model.cost_tier || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={model.is_enabled ? 'Enabled' : 'Disabled'}
                                                              size="small"
                                                              color={model.is_enabled ? 'success' : 'default'} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {model.created_at ? new Date(model.created_at).toLocaleDateString() : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Check Availability">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleValidateModel(model.id)}
                                                                disabled={validatingModel === model.id}
                                                            >
                                                                {validatingModel === model.id ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <CheckCircleIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <IconButton size="small" onClick={() => handleEditModel(provider.id, model)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => handleDeleteModel(model.id)} color="error">
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center">
                                                    <Typography variant="body2" color="text.secondary">No models configured</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                ))
            )}

            {/* Provider Dialog */}
            {providerDialog && (
                <Dialog open onClose={() => setProviderDialog(null)} maxWidth="sm" fullWidth>
                    <DialogTitle>{providerDialog.mode === 'create' ? 'Create Provider' : 'Edit Provider'}</DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <TextField
                                label="Provider Name (lowercase, e.g., openai)"
                                value={providerDialog.data.name}
                                onChange={(e) => setProviderDialog({ ...providerDialog, data: { ...providerDialog.data, name: e.target.value.toLowerCase() } })}
                                disabled={providerDialog.mode === 'edit'}
                                required
                            />
                            <TextField
                                label="Display Name"
                                value={providerDialog.data.display_name}
                                onChange={(e) => setProviderDialog({ ...providerDialog, data: { ...providerDialog.data, display_name: e.target.value } })}
                                required
                            />
                            <TextField
                                label="Description"
                                value={providerDialog.data.description || ''}
                                onChange={(e) => setProviderDialog({ ...providerDialog, data: { ...providerDialog.data, description: e.target.value } })}
                                multiline
                                rows={2}
                            />
                            <TextField
                                label="Sort Order"
                                type="number"
                                value={providerDialog.data.sort_order}
                                onChange={(e) => setProviderDialog({ ...providerDialog, data: { ...providerDialog.data, sort_order: parseInt(e.target.value) || 0 } })}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={providerDialog.data.is_enabled}
                                        onChange={(e) => setProviderDialog({ ...providerDialog, data: { ...providerDialog.data, is_enabled: e.target.checked } })}
                                    />
                                }
                                label="Enabled"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setProviderDialog(null)}>Cancel</Button>
                        <Button onClick={handleSaveProvider} variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Model Dialog */}
            {modelDialog && (
                <Dialog open onClose={() => setModelDialog(null)} maxWidth="md" fullWidth>
                    <DialogTitle>{modelDialog.mode === 'create' ? 'Create Model' : 'Edit Model'}</DialogTitle>
                    <DialogContent>
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={1}>
                            <TextField
                                label="Model Name (e.g., gpt-4o)"
                                value={modelDialog.data.name}
                                onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, name: e.target.value } })}
                                disabled={modelDialog.mode === 'edit'}
                                required
                            />
                            <TextField
                                label="Display Name"
                                value={modelDialog.data.display_name}
                                onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, display_name: e.target.value } })}
                                required
                            />
                            <TextField
                                label="Description"
                                value={modelDialog.data.description || ''}
                                onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, description: e.target.value } })}
                                multiline
                                rows={2}
                                sx={{ gridColumn: '1 / -1' }}
                            />
                            <FormControl>
                                <InputLabel>Performance Tier (1-5)</InputLabel>
                                <Select
                                    value={modelDialog.data.performance_tier || ''}
                                    onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, performance_tier: e.target.value || null } })}
                                    label="Performance Tier (1-5)"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {[1, 2, 3, 4, 5].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <InputLabel>Efficiency Tier (1-5)</InputLabel>
                                <Select
                                    value={modelDialog.data.efficiency_tier || ''}
                                    onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, efficiency_tier: e.target.value || null } })}
                                    label="Efficiency Tier (1-5)"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {[1, 2, 3, 4, 5].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <InputLabel>Cost Tier (1-5)</InputLabel>
                                <Select
                                    value={modelDialog.data.cost_tier || ''}
                                    onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, cost_tier: e.target.value || null } })}
                                    label="Cost Tier (1-5)"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {[1, 2, 3, 4, 5].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Context Window"
                                type="number"
                                value={modelDialog.data.context_window || ''}
                                onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, context_window: parseInt(e.target.value) || null } })}
                            />
                            <TextField
                                label="Sort Order"
                                type="number"
                                value={modelDialog.data.sort_order}
                                onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, sort_order: parseInt(e.target.value) || 0 } })}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={modelDialog.data.is_enabled}
                                        onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, is_enabled: e.target.checked } })}
                                    />
                                }
                                label="Enabled"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={modelDialog.data.is_default}
                                        onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, is_default: e.target.checked } })}
                                    />
                                }
                                label="Default Model"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={modelDialog.data.supports_vision}
                                        onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, supports_vision: e.target.checked } })}
                                    />
                                }
                                label="Supports Vision"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={modelDialog.data.supports_function_calling}
                                        onChange={(e) => setModelDialog({ ...modelDialog, data: { ...modelDialog.data, supports_function_calling: e.target.checked } })}
                                    />
                                }
                                label="Supports Function Calling"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setModelDialog(null)}>Cancel</Button>
                        <Button onClick={handleSaveModel} variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
}
